import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/AuthContext';
import { SEOHead } from '../components/SEOHead';
import { StatPill } from '../components/trainer/StatPill';
import { Button } from '../components/ui';
import { useTrainerClients } from '../lib/useTrainerClients';
import { useTrainerExercises } from '../lib/useTrainerExercises';
import { useIsMobile } from '../lib/useIsMobile';
import { useTrainerSessions } from '../lib/useTrainerSessions';
import { useTrainerTemplates } from '../lib/useTrainerTemplates';
import {
  ClientsOverview,
  QuickActions,
  UpcomingSessions,
} from './trainer-dashboard';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function TrainerDashboard() {
  const { userData, signOut, user } = useAuth();
  const navigate = useNavigate();
  const trainerId = useMemo(
    () => userData?.trainerProfileId || userData?.trainerId || user?.uid,
    [userData, user]
  );
  const isMobile = useIsMobile();

  useEffect(() => {
    if (trainerId && userData && !userData.displayName?.trim()) {
      navigate('/trainer/onboarding');
    }
  }, [trainerId, userData, navigate]);

  const { exercises } = useTrainerExercises(trainerId);
  const { templates, assignments, upcomingAssignments } = useTrainerTemplates(trainerId);
  const { clients } = useTrainerClients(trainerId);
  const { sessions } = useTrainerSessions(trainerId);

  const clientSummaries = useMemo(
    () =>
      clients.map((client) => ({
        id: client.id,
        displayName: client.displayName || client.phone || 'Unnamed client',
        phone: client.phone,
        status: client.status,
      })),
    [clients]
  );

  const linkedClientCount = useMemo(
    () => clients.filter((client) => client.status === 'linked').length,
    [clients]
  );

  const pendingClientCount = useMemo(
    () => clients.filter((client) => client.status === 'pending').length,
    [clients]
  );

  const clientNameById = useMemo(
    () =>
      new Map(
        clients.map((client) => [
          client.id,
          client.displayName || client.phone || 'Unknown client',
        ])
      ),
    [clients]
  );

  const templateNameById = useMemo(
    () => new Map(templates.map((template) => [template.id, template.name])),
    [templates]
  );

  const upcomingSessionSummaries = useMemo(
    () =>
      upcomingAssignments
        .filter((assignment) => !['completed', 'skipped', 'missed'].includes(assignment.status))
        .slice(0, 5)
        .map((assignment) => ({
          id: assignment.id,
          clientName: clientNameById.get(assignment.clientId) || 'Unknown client',
          workoutName:
            assignment.workoutTemplateName ||
            templateNameById.get(assignment.workoutTemplateId) ||
            'Workout template',
          scheduledFor: DATE_FORMATTER.format(assignment.scheduledFor.toDate()),
          status: assignment.status,
        })),
    [clientNameById, templateNameById, upcomingAssignments]
  );

  if (!trainerId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Trainer not linked</p>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            Your account is missing a trainerId claim. Sign out and re-open your
            secret link, or create the trainer via Cloud Function.
          </p>
          <Button variant="secondary" onClick={signOut} className="mt-4">
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Trainer Dashboard | Start Grounded"
        description="Review clients, upcoming sessions, and your trainer tools"
        canonicalUrl="https://startgrounded.app/trainer"
      />
      <div className="p-6" data-testid="trainer-dashboard" data-trainer-id={trainerId}>
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm text-primary-600 dark:text-primary-400 uppercase tracking-wide font-semibold">
                Trainer Overview
              </p>
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                {userData?.displayName || 'Trainer'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Keep tabs on clients, sessions, and your workout library from one place.
              </p>
            </div>
          </div>

          {!isMobile && (
            <div className="grid gap-3 sm:grid-cols-4">
              <StatPill label="Clients" value={clients.length} />
              <StatPill label="Upcoming" value={upcomingSessionSummaries.length} />
              <StatPill label="Templates" value={templates.length} />
              <StatPill label="Exercises" value={exercises.length} />
            </div>
          )}

          <div className="grid gap-6 xl:grid-cols-12">
            <div className="xl:col-span-5">
              <ClientsOverview
                clients={clientSummaries}
                linkedCount={linkedClientCount}
                pendingCount={pendingClientCount}
                onViewAllClients={() => navigate('/trainer/clients')}
              />
            </div>
            <div className="xl:col-span-7">
              <UpcomingSessions
                assignments={upcomingSessionSummaries}
                totalAssignments={assignments.length}
                onViewAllSessions={() => navigate('/trainer/sessions')}
              />
            </div>
            <div className="xl:col-span-12">
              <QuickActions
                exerciseCount={exercises.length}
                templateCount={templates.length}
                assignmentCount={assignments.length}
                sessionCount={sessions.length}
                onOpenWorkouts={() => navigate('/trainer/workouts')}
                onOpenClients={() => navigate('/trainer/clients')}
                onOpenSessions={() => navigate('/trainer/sessions')}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
